import React from 'react';
import {
    Box,
    Button,
    Tooltip,
    List,
    ListItem,
    Typography
} from "@mui/material";
import {
    Error as ErrorIcon,
    OpenInNew as OpenInNewIcon
} from "@mui/icons-material";
import _ from "lodash";

const styles = {
    sourceButton: {
        textTransform: 'none',
        fontSize: '12px',
        height: '24px',
        fontWeight: 400,
        minWidth: '94px',
        padding: '12px 12px',
        borderRadius: '12px',
        backgroundColor: '#ffffff',
        color: '#374151',
        border: '1.5px solid #E2E6F0',
        '&:hover': {
            backgroundColor: '#ffffff',
            boxShadow: 'none'
        },
        '&:focus': {
            boxShadow: 'none'
        }
    },
    popoverBox: {
        maxWidth: 500,
        maxHeight: 300,
        overflowY: 'auto',
        backgroundColor: '#ffffff',
        color: 'black',
        padding: "8px"
    },
    popoverList: {
        px: 2
    },
    popoverItem: {
        display: "flex",
        alignItems: "center",
        padding: "4px",
        borderRadius: "6px",
        transition: "background-color 0.2s ease"
    },
    popoverListItem: {
        px: 1,
        width: "100%"
    },
    clickableItem: {
        cursor: 'default',
        '&:hover': {
            backgroundColor: '#f3f4f6'
        }
    }
};

const SourceButton = ({ sources = [] }) => {
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const timeoutRef = React.useRef(null);
    const anchorRef = React.useRef(null);

    // Clear any existing timeout
    const clearHideTimeout = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            clearHideTimeout();
        };
    }, []);

    // Filter sources to only include those with URLs
    const sourcesWithUrls = sources.filter(source => {
        // Allow sources with URLs
        if (source.source_url && source.source_url.includes('http')) {
            return true;
        }

        // Also allow Secretary of State sources (SOS variations) and known data source names
        if (source.source_url && typeof source.source_url === 'string') {
            const url = source.source_url.toLowerCase();
            if (url.includes('sos') ||
                url.includes('secretary of state') ||
                url.includes('secretary') ||
                url.includes('state filing') ||
                url.includes('state registration') ||
                url.includes('lexisnexis') ||
                url.includes('kyb-compliant') ||
                url === 'linkedin') {
                return true;
            }
        }

        return false;
    }).map(source => {
        // Transform LexisNexis to "KYB-compliant database"
        if (source.source_url && source.source_url.toLowerCase().includes('lexisnexis')) {
            return {
                ...source,
                source_url: 'KYB-compliant database'
            };
        }

        if (source.source_url && source.source_url.includes('http')) {
            return {
                ...source,
                source_url: source.source_url.replace('http://', 'https://')
            };
        }

        // Normalize all SOS variations to a single canonical string
        if (source.source_url && typeof source.source_url === 'string') {
            const url = source.source_url.toLowerCase();
            if (url === 'sos' || url === 'sos_url' ||
                url.includes('secretary of state') ||
                url.includes('secretary') ||
                url.includes('state filing') ||
                url.includes('state registration') ||
                url.includes('opencorporates')) {
                return {
                    ...source,
                    source_url: 'Secretary of State'
                };
            }
            if (url === 'linkedin') {
                return {
                    ...source,
                    source_url: 'LinkedIn'
                };
            }
        }

        return source;
    });

    // Don't render anything if no sources with URLs
    if (sourcesWithUrls.length === 0) {
        return null;
    }

    // Simple container approach with immediate timeout
    const handleContainerMouseEnter = () => {
        console.log('Mouse entered container'); // Debug log
        clearHideTimeout();
        setPopoverOpen(true);
    };

    const handleContainerMouseLeave = () => {
        console.log('Mouse left container, setting timeout'); // Debug log
        clearHideTimeout();
        timeoutRef.current = setTimeout(() => {
            console.log('Timeout fired, closing popover'); // Debug log
            setPopoverOpen(false);
        }, 150);
    };

    const handleItemClick = (source, e) => {
        e.stopPropagation();
        if (source.source_url && source.source_url.includes('http')) {
            window.open(source.source_url, '_blank', 'noopener,noreferrer');
        }
    };

    // Transform sources data for display and remove duplicates
    const sourceItems = _.uniqBy(_.map(sourcesWithUrls, (source, index) => {
        // Extract meaningful source name from source_url
        let sourceName = source.source_url || `Source ${index + 1}`;

        // Clean up common source names
        if (sourceName === 'company_website') {
            sourceName = 'Company Website';
        } else if (sourceName === 'business_directory') {
            sourceName = 'Business Directory';
        } else if (sourceName === 'manual_input') {
            sourceName = 'Manual Input';
        } else if (sourceName.includes('http')) {
            // Extract domain from URL
            try {
                const url = new URL(sourceName);
                sourceName = url.hostname.replace('www.', '');
            } catch {
                sourceName = sourceName.slice(0, 30) + '...';
            }
        }

        return {
            id: sourceName,
            hasError: false,
            fetched_at: source.fetched_at,
            isClickable: source.source_url && source.source_url.includes('http'),
            originalSource: source
        };
    }), 'id');

    return (
        <Box
            onMouseEnter={handleContainerMouseEnter}
            onMouseLeave={handleContainerMouseLeave}
            sx={{ display: 'inline-block', width: 'fit-content', height: 'fit-content' }}
        >
            <Tooltip
                open={popoverOpen}
                title={
                    <Box sx={styles.popoverBox}>
                        <List sx={styles.popoverList} disablePadding>
                            {sourceItems.length > 0 ? (
                                _.map(sourceItems, (item, index) => {
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                ...styles.popoverItem,
                                                ...(item.isClickable ? styles.clickableItem : {})
                                            }}
                                            onClick={item.isClickable ? (e) => handleItemClick(item.originalSource, e) : undefined}
                                        >
                                            <ListItem sx={styles.popoverListItem} disablePadding>
                                                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: item.isClickable ? '#1976d2' : 'inherit',
                                                                fontSize: '13px',
                                                                lineHeight: 1.3
                                                            }}
                                                        >
                                                            {item.id}
                                                        </Typography>
                                                        {item.fetched_at && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: '#6b7280',
                                                                    fontSize: '11px',
                                                                    display: 'block'
                                                                }}
                                                            >
                                                                {new Date(item.fetched_at).toLocaleDateString()}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {item.isClickable && (
                                                        <OpenInNewIcon sx={{ fontSize: 14, color: '#9ca3af', ml: 1 }} />
                                                    )}
                                                </Box>
                                            </ListItem>
                                        </Box>
                                    );
                                })
                            ) : (
                                <Box sx={styles.popoverItem}>
                                    <ErrorIcon color="error" sx={{ fontSize: 16, mr: 1 }} />
                                    <ListItem sx={styles.popoverListItem}>
                                        <Typography variant="body2">No sources available</Typography>
                                    </ListItem>
                                </Box>
                            )}
                        </List>
                    </Box>
                }
                placement="bottom-end"
                arrow={false}
                slotProps={{
                    tooltip: {
                        sx: {
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px', // More rounded corners
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                            padding: 0,
                            maxWidth: 'none',
                            width: 'fit-content',
                            zIndex: 99999999
                        }
                    }
                }}
                PopperProps={{
                    disablePortal: false,
                    sx: {
                        zIndex: 99999999
                    },
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, -2] // Negative Y value to bring tooltip closer
                            }
                        }
                    ]
                }}
            >
                <Button
                    ref={anchorRef}
                    variant="outlined"
                    size="small"
                    sx={styles.sourceButton}
                >
                    Sources
                </Button>
            </Tooltip>
        </Box>
    );
};

export default SourceButton;